import React, { useState } from "react";
import { Row, Col, Input, Select, Space, Typography, Spin, DatePicker } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ProposalModel } from "../model/proposals.ui";
import { ProposalDetail } from "../model/proposalDetail.ui";
import { MobilePullToRefreshList } from "../../../components/mobile/MobilePullToRefreshList";
import { ProposalDesktopTable } from "../components/ProposalDesktopTable";
import { ProposalMobileCard } from "../components/ProposalMobileCard";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface ProposalListPageProps {
  proposals: ProposalModel[];
  isDesktop: boolean;
  onSelectProposal: (proposal: ProposalModel) => void;
  onRefresh?: () => Promise<void>;
  onLoadMore?: () => Promise<void>;
  onPageChange?: (page: number) => Promise<void>;
  isLoading?: boolean;
  isRefreshing?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  searchText?: string;
  onSearchChange?: (val: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (val: string) => void;
  dateRange?: [string, string] | null;
  onDateRangeChange?: (dates: [string, string] | null) => void;
  selectedProposal?: ProposalModel | null;
  proposalDetail?: ProposalDetail | null;
  isDetailLoading?: boolean;
  onCloseDetail?: () => void;
}

export const ProposalListPage: React.FC<ProposalListPageProps> = ({
  proposals = [],
  isDesktop,
  onSelectProposal,
  onRefresh,
  onLoadMore,
  onPageChange,
  isLoading = false,
  isRefreshing = false,
  isLoadingMore = false,
  hasMore = false,
  currentPage = 1,
  totalPages = 1,
  totalItems,
  searchText = "",
  onSearchChange,
  statusFilter = "all",
  onStatusFilterChange,
  dateRange: propDateRange,
  onDateRangeChange,
}) => {
  const [internalDateRange, setInternalDateRange] = useState<[string, string] | null>(null);

  const dateRange =
    propDateRange !== undefined ? propDateRange : internalDateRange;

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    const val: [string, string] | null = dates && dateStrings[0] && dateStrings[1] ? [dateStrings[0], dateStrings[1]] : null;
    if (onDateRangeChange) {
      onDateRangeChange(val);
    } else {
      setInternalDateRange(val);
    }
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      await onRefresh();
    }
  };

  const handleLoadMore = async () => {
    if (onLoadMore) {
      await onLoadMore();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div style={{ flexShrink: 0, marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={9}>
            <Input
              placeholder="提案番号や担当者IDで検索..." /* Tìm kiếm theo mã đề xuất hoặc ID người phụ trách... */
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={searchText}
              onChange={(e) => onSearchChange?.(e.target.value)}
              allowClear
              size={isDesktop ? "large" : "middle"}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: "100%" }}
              size={isDesktop ? "large" : "middle"}
              value={statusFilter}
              onChange={(val) => onStatusFilterChange?.(val)}
              options={[
                { value: "all", label: "すべてのステータス" /* Tất cả trạng thái */ },
                { value: "DRAFT", label: "下書き" /* Bản nháp */ },
                { value: "SENT", label: "送付済" /* Đã gửi */ },
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={9}>
            {isDesktop ? (<RangePicker
              style={{ width: "100%", borderRadius: 8 }}
              size={isDesktop ? "large" : "middle"}
              placeholder={["開始日", "終了日"]} /* Ngày bắt đầu, Ngày kết thúc */
              value={
                dateRange && dateRange[0] && dateRange[1]
                  ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                  : null
              }
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />) : (
              <Row gutter={8}>
                <Col span={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="開始日" /* Ngày bắt đầu */
                    format="YYYY-MM-DD"
                    value={dateRange && dateRange[0] ? dayjs(dateRange[0]) : null}
                    onChange={(date) => {
                      const fromStr = date ? date.format("YYYY-MM-DD") : "";
                      const toStr = dateRange && dateRange[1] ? dateRange[1] : "";
                      const val: [string, string] | null =
                        !fromStr && !toStr ? null : [fromStr, toStr];
                      if (onDateRangeChange) onDateRangeChange(val);
                      else setInternalDateRange(val);
                    }}
                    allowClear
                  />
                </Col>
                <Col span={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="終了日" /* Ngày kết thúc */
                    format="YYYY-MM-DD"
                    value={dateRange && dateRange[1] ? dayjs(dateRange[1]) : null}
                    onChange={(date) => {
                      const fromStr = dateRange && dateRange[0] ? dateRange[0] : "";
                      const toStr = date ? date.format("YYYY-MM-DD") : "";
                      const val: [string, string] | null =
                        !fromStr && !toStr ? null : [fromStr, toStr];
                      if (onDateRangeChange) onDateRangeChange(val);
                      else setInternalDateRange(val);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </div>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {isDesktop ? (
          <ProposalDesktopTable
            proposals={proposals}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onSelectProposal={onSelectProposal}
          />
        ) : isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" description="提案一覧を読み込み中..." /* Đang tải danh sách đề xuất... */ />
          </div>
        ) : (
          <MobilePullToRefreshList
            onRefresh={handleRefresh}
            onLoadMore={handleLoadMore}
            isRefreshing={isRefreshing}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalItems={totalItems ?? proposals.length}
            currentItemsCount={proposals.length}
          >
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
              {proposals.length === 0 ? (
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    textAlign: "center",
                    padding: 20,
                  }}
                >
                  該当する提案がありません。{/* Không có đề xuất phù hợp. */}
                </Text>
              ) : (
                proposals.map((item) => (
                  <ProposalMobileCard
                    key={item.propose_id || item.propose_code}
                    proposal={item}
                    onSelect={onSelectProposal}
                  />
                ))
              )}
            </Space>
          </MobilePullToRefreshList>
        )}
      </div>
    </div>
  );
};

export default ProposalListPage;
